import { zodResolver } from '@hookform/resolvers/zod';
import type { input, output } from 'zod';
import { Button, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { useForm } from 'react-hook-form';
import { DialogType } from '../../../../../@types';
import { QUERY_KEYS } from '../../../../../constants';
import { useInvalidateQuery } from '../../../../../hooks/useInvalidateQuery';
import useMutateData from '../../../../../hooks/useMutateData';
import { handleCreditsSchema } from '../../../../../schema';
import FormNumberInput from '../../../../molecules/FormNumberInput';

const schema = handleCreditsSchema.pick({ amount: true });
type AddAdminCreditFormInput = input<typeof schema>;
type AddAdminCreditFormData = output<typeof schema>;

const DEFAULT_FORM_DATA: AddAdminCreditFormInput = {
  amount: '',
};

type Props = DialogType & {
  item: string;
};

const AddAdminCreditsForm = ({ closeModal, item: userId }: Props) => {
  const invalidateData = useInvalidateQuery();

  const { createData } = useMutateData('wallets/credits', [QUERY_KEYS.credits, userId], 'wallet');
  const { control, handleSubmit } = useForm<AddAdminCreditFormInput, unknown, AddAdminCreditFormData>({
    defaultValues: DEFAULT_FORM_DATA,
    resolver: zodResolver(schema),
  });

  const handleClose = () => {
    closeModal?.();
  };

  const onFormSubmit = (creditsData: AddAdminCreditFormData) => {
    const preparedData = {
      amount: creditsData.amount,
    };

    createData(
      preparedData,
      () => {
        invalidateData([QUERY_KEYS.userBalance]);
        invalidateData([QUERY_KEYS.adminTransactions]);
        handleClose();
      },
      'Credits added successfully'
    );
  };

  return (
    <>
      <DialogTitle variant='h4' gutterBottom>
        Add Credits
      </DialogTitle>
      <DialogContent>
        <FormNumberInput
          name='amount'
          control={control}
          label='Amount'
          allowDecimals
          valueAsString
          required
          fullWidth
        />
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' onClick={handleClose}>
          Cancel
        </Button>
        <Button onClick={handleSubmit(onFormSubmit)}>Add</Button>
      </DialogActions>
    </>
  );
};

export default AddAdminCreditsForm;
